import React, { useEffect, useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LanguageProvider } from "./screens/Authentication/LanguageContext";
import { AuthProvider } from "./screens/Authentication/AuthContext";
import { FontSizeProvider } from "./screens/Authentication/FontSizeContext";
import SiteLanguageBridge from "./i18n/SiteLanguageBridge";
import Header from "./screens/Header/Header";
import Footer from "./screens/Footer/Footer";
import MainScreen from "./screens/MainScreen/MainScreen";
import PrivacyPolicy from "./screens/Header/Menus/PrivacyPolicy";
import TermsOfService from "./screens/Header/Menus/TermsOfService";
import AuthAction from "./screens/Authentication/AuthAction/AuthAction";
import Profile from "./screens/Authentication/Login/Profile";
import CoursePage from "./screens/MainScreen/Courses/CoursePage";
import BooksPage from "./screens/MainScreen/Books/BooksPage";
import BookViewPage from "./screens/MainScreen/Books/BookViewPage";
import Contact from "./screens/Header/Menus/Contact";
import "./screens/MainScreen/MainStackStyles.css";
import "./App.css";
import "./styles/MobileParity.css";
import "./styles/MobileExact.css";
import "./styles/LegacyMobileHome.css";
import "./styles/WebsiteLanguageFontPolish.css";
import "./styles/WebsiteMultilingualFonts.css";
import "./styles/WebsiteDrivingGlassCards.css";
import "./styles/UniformWebsiteTypography.css";
import "./styles/WebsiteUniformFontLayout.css";
import "./styles/WebsiteHomeAdaptiveFill.css";
import "./styles/DearLanguageEditorial.css";
import "./styles/DearLanguageLiquidGlassPremium.css";
import "./styles/DearLanguageLiquidGlassEverywhere.css";
import "./styles/DearLanguageHomeMatched.css";
import "./styles/DearLanguageBorderless.css";
import "./styles/DearLanguageFluidBars.css";
import "./styles/DearLanguageLocalizedLayout.css";
import "./styles/DearLanguageHeroDailyRestore.css";
import "./styles/DearLanguageHeroResponsivePolish.css";
import "./styles/DearLanguageMobileHeroFix.css";
import "./styles/DearLanguageHomeMobileSimplify.css";
import "./styles/DearLanguageHeroAlignmentFinal.css";
import "./styles/DearLanguageHeroGlassBlueFinal.css";
import "./styles/DearLanguageHeroDrivingGlassAuto.css";
import "./styles/DearLanguageHeroQRCodeDownloads.css";
import "./styles/DearLanguageTwoHomeCards.css";
import "./styles/DearLanguageBilingualGermanFirst.css";
import "./styles/DearLanguageHeroMotionPolish.css";
import "./styles/DearLanguageHeroSignature.css";
import "./styles/DearLanguageHeroEditorialV2.css";
import "./styles/DearLanguageHeroSwappedLayout.css";
import "./styles/DearLanguageHeroLeftPremium.css";
import "./styles/DearLanguageHeroScreensaverLeftFinal.css";
import "./styles/DearLanguageHeroTypewriterFinal.css";
import "./styles/DearLanguageHeroMarketingIntro.css";
import "./styles/DearLanguageHeroContentAutoHeight.css";

export type WebsiteTheme = "light" | "dark";

const WEBSITE_THEME_KEY = "dear_language_website_theme";

const normalizeTheme = (value: string | null): WebsiteTheme => {
  return value === "dark" ? "dark" : "light";
};

const getStoredTheme = (): WebsiteTheme => {
  try {
    return normalizeTheme(window.localStorage.getItem(WEBSITE_THEME_KEY));
  } catch {
    return "light";
  }
};

const applyTheme = (theme: WebsiteTheme): void => {
  document.documentElement.setAttribute("data-theme", theme);

  document.body.classList.remove("website-theme-light", "website-theme-dark");
  document.body.classList.add(`website-theme-${theme}`);

  try {
    window.localStorage.setItem(WEBSITE_THEME_KEY, theme);
  } catch {
    // localStorage can be blocked.
  }

  window.dispatchEvent(
    new CustomEvent("dear-language-theme-change", {
      detail: { theme },
    }),
  );
};

// Main navigation is defined here so a missing/incorrectly installed
// screens/MainScreen/MainStack.tsx cannot prevent the application from compiling.
// The original MainStack.tsx is retained in the archive for compatibility.
const normalizePathname = (pathname: string): string => {
  const cleanPathname = String(pathname || "/")
    .trim()
    .toLowerCase();

  if (!cleanPathname || cleanPathname === "/") {
    return "/";
  }

  return cleanPathname.replace(/\/+$/, "");
};

const getRouteKey = (pathname: string): string => {
  const cleanPathname = normalizePathname(pathname);

  if (cleanPathname === "/") {
    return "startseite";
  }

  if (cleanPathname === "/courses") {
    return "kurse";
  }

  if (cleanPathname === "/books") {
    return "buecher";
  }

  if (cleanPathname === "/book-view") {
    return "buch-ansicht";
  }

  if (cleanPathname === "/contact") {
    return "kontakt";
  }

  if (cleanPathname === "/profile") {
    return "profil";
  }

  if (cleanPathname === "/auth/action") {
    return "auth-action";
  }

  if (cleanPathname === "/privacy-policy") {
    return "datenschutz";
  }

  if (cleanPathname === "/terms-of-service") {
    return "nutzungsbedingungen";
  }

  return cleanPathname.replace(/[^a-z0-9]+/g, "-") || "seite";
};

const clearPageClasses = (): void => {
  const classesToRemove = [
    "main-screen-page",
    "course-screen-page",
    "courses-screen-page",
    "books-screen-page",
    "book-view-screen-page",
    "contact-screen-page",
    "privacy-screen-page",
    "terms-screen-page",
    "content-screen-page",
    "profile-screen-page",
  ];

  document.documentElement.classList.remove(...classesToRemove);
  document.body.classList.remove(...classesToRemove);
};

const MainStack: React.FC = () => {
  const location = useLocation();

  const cleanPathname = useMemo(() => {
    return normalizePathname(location.pathname);
  }, [location.pathname]);

  const routeKey = useMemo(() => {
    return getRouteKey(location.pathname);
  }, [location.pathname]);

  const renderKey = useMemo(() => {
    if (cleanPathname === "/book-view") {
      return `${routeKey}-${location.search || "default"}-${
        location.key || "direct"
      }`;
    }

    return `${routeKey}-${location.key || "direct"}`;
  }, [cleanPathname, location.key, location.search, routeKey]);

  useEffect(() => {
    clearPageClasses();

    if (cleanPathname === "/") {
      document.documentElement.classList.add("main-screen-page");
      document.body.classList.add("main-screen-page");
    }

    if (cleanPathname === "/courses") {
      document.documentElement.classList.add("course-screen-page");
      document.body.classList.add("course-screen-page");
    }

    if (cleanPathname === "/books") {
      document.documentElement.classList.add("books-screen-page");
      document.body.classList.add("books-screen-page");
    }

    if (cleanPathname === "/book-view") {
      document.documentElement.classList.add("book-view-screen-page");
      document.body.classList.add("book-view-screen-page");
    }

    if (cleanPathname === "/contact") {
      document.documentElement.classList.add("contact-screen-page");
      document.body.classList.add("contact-screen-page");
    }

    if (cleanPathname === "/profile") {
      document.documentElement.classList.add("profile-screen-page");
      document.body.classList.add("profile-screen-page");
    }

    if (cleanPathname === "/privacy-policy") {
      document.documentElement.classList.add("privacy-screen-page");
      document.body.classList.add("privacy-screen-page");
    }

    if (cleanPathname === "/terms-of-service") {
      document.documentElement.classList.add("terms-screen-page");
      document.body.classList.add("terms-screen-page");
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    window.dispatchEvent(
      new CustomEvent("dear-language-route-change", {
        detail: {
          pathname: cleanPathname,
          search: location.search,
          routeKey,
        },
      }),
    );

    if (cleanPathname === "/courses") {
      window.dispatchEvent(new CustomEvent("dear-language-course-reset"));
      window.dispatchEvent(new CustomEvent("dear-language-course-page-opened"));
    }

    if (cleanPathname === "/books") {
      window.dispatchEvent(new CustomEvent("dear-language-books-page-opened"));
    }

    if (cleanPathname === "/book-view") {
      window.dispatchEvent(new CustomEvent("dear-language-book-view-opened"));
    }

    if (cleanPathname === "/contact") {
      window.dispatchEvent(new CustomEvent("dear-language-contact-page-opened"));
    }

    if (cleanPathname === "/profile") {
      window.dispatchEvent(new CustomEvent("dear-language-profile-page-opened"));
    }

    return () => {
      clearPageClasses();
    };
  }, [cleanPathname, location.search, routeKey]);

  return (
    <div
      className={`mainWrapper main-route-${routeKey}`}
      data-current-route={cleanPathname}
    >
      <Header />

      <main
        key={renderKey}
        className="mainRouteContent"
        data-route={cleanPathname}
      >
        <Routes location={location} key={renderKey}>
          <Route path="/" element={<MainScreen />} />
          <Route path="/courses" element={<CoursePage />} />
          <Route path="/books" element={<BooksPage />} />
          <Route path="/book-view" element={<BookViewPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/auth/action" element={<AuthAction />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    applyTheme(getStoredTheme());
    // Loading optional script fonts must not block startup or offline users.
    const fontLinkId = "dear-language-multilingual-fonts";
    if (!document.getElementById(fontLinkId)) {
      const fontLink = document.createElement("link");
      fontLink.id = fontLinkId;
      fontLink.rel = "stylesheet";
      fontLink.href = "https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;600;700&family=Noto+Sans+Devanagari:wght@400;500;600;700&family=Noto+Sans+SC:wght@400;500;600;700&family=Noto+Sans+Sinhala:wght@400;500;600;700&family=Noto+Sans+Tamil:wght@400;500;600;700&display=swap";
      fontLink.media = "print";
      fontLink.addEventListener("load", () => { fontLink.media = "all"; }, {once: true});
      document.head.appendChild(fontLink);
    }
  }, []);

  return (
    <LanguageProvider>
      <FontSizeProvider>
      <AuthProvider>
        <BrowserRouter>
          <MainStack />
          <SiteLanguageBridge />
        </BrowserRouter>
      </AuthProvider>
      </FontSizeProvider>
    </LanguageProvider>
  );
};

export default App;